import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { paymentsService } from '../../services/paymentsService';
import {
  getPlanFeatures, getPlanLimits, formatLimit, formatStorageLimit,
  normalizePlanKey, PLAN_DISPLAY, PLAN_ORDER,
} from '../../config/planFeatures';

// ─── Plan metadata ────────────────────────────────────────────────────────────
const PLAN_META = Object.fromEntries(
  PLAN_ORDER.map((key) => {
    const plan = PLAN_DISPLAY[key];
    return [key, { ...plan, color: plan.accentColor, badge: plan.badgeClasses }];
  }),
);

// ─── Left branding panel ──────────────────────────────────────────────────────
const GENERIC_CHIPS = [
  { icon: 'Zap',     label: 'Lightning Fast',    description: 'Optimized performance' },
  { icon: 'Palette', label: 'Fully Customizable', description: 'Complete design control' },
  { icon: 'Code2',   label: 'Easy Embedding',     description: 'Deploy anywhere' },
  { icon: 'Shield',  label: 'Secure by Default',  description: 'Encrypted & access-controlled' },
];

const BrandingPanel = ({ planName, isPaid, planKey = 'free', limits }) => {
  const planLimits   = limits || getPlanLimits(planKey);
  const planFeatures = getPlanFeatures(planKey).slice(0, 8); // top 8 features

  return (
    <div className="hidden lg:flex lg:w-1/2 bg-[#ecf5ee] relative overflow-hidden flex-col justify-between p-8 lg:p-12">
      <div className="relative z-10 flex flex-col justify-between h-full">
        {/* Logo */}
        <div className="flex items-center gap-3 mt-24">
          <div className="w-56 h-auto rounded-md overflow-hidden">
            <Image
              src="/assets/images/web-forms-logo-1769801123836.png"
              alt="WebForms.biz logo"
              className="w-56 h-auto object-contain"
            />
          </div>
        </div>

        {/* Headline */}
        <div className="space-y-5 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-sm font-medium text-primary">
            <Icon name="Sparkles" size={14} />
            {isPaid ? `${planName} Plan` : 'Free Account'}
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
            {isPaid ? `Unlock ${planName} Power` : 'Start Building\nForms for Free'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isPaid
               ? 'Start with a 14-day free trial — no credit card required. If you continue, you are covered by our 30-day money-back guarantee.'
              : 'Create forms, collect submissions, and grow your business — no credit card required.'}
          </p>

          {/* Plan limit chips */}
          <div className="flex flex-wrap gap-2">
            {[
              { icon: 'FileText', label: `${formatLimit(planLimits.maxForms)} Forms` },
              { icon: 'Inbox',    label: `${formatLimit(planLimits.maxSubmissions)} Submissions/mo` },
              { icon: 'Users',    label: `${formatLimit(planLimits.maxTeamMembers)} ${planLimits.maxTeamMembers === 1 ? 'Seat' : 'Seats'}` },
              { icon: 'HardDrive', label: formatStorageLimit(planLimits.maxStorageMb) },
            ].map((chip, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-xs font-semibold text-primary">
                <Icon name={chip.icon} size={11} />
                {chip.label}
              </span>
            ))}
          </div>

          {/* Plan feature list (paid) or generic chips (free) */}
          {isPaid ? (
            <div className="space-y-2 pt-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">What's included:</p>
              {planFeatures.map((feat) => (
                <div key={feat.key} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-success/10 border border-success/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={11} className="text-success" />
                  </div>
                  <span className="text-sm text-foreground">{feat.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 pt-1">
              {GENERIC_CHIPS.map((f, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                    <Icon name={f.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{f.label}</div>
                    <div className="text-xs text-muted-foreground">{f.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};


// ─── Free account form (no Microsoft button) ──────────────────────────────────
const FreeSignupForm = ({ onSuccess, redirectTo = '/dashboard' }) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!formData?.fullName?.trim() || formData?.fullName?.trim()?.length < 2)
    e.fullName = 'Full name must be at least 2 characters';
    if (!formData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email))
    e.email = 'Please enter a valid email address';
    if (!formData?.password || formData?.password?.length < 8)
    e.password = 'Password must be at least 8 characters';
    if (formData?.password !== formData?.confirmPassword)
    e.confirmPassword = 'Passwords do not match';
    if (!formData?.agreeToTerms)
    e.agreeToTerms = 'You must agree to the terms and conditions';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (authError) setAuthError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const data = await signUp(formData?.email, formData?.password, {
        full_name: formData?.fullName?.trim()
      });
      if (onSuccess) onSuccess();
      if (data?.session) navigate(redirectTo, { replace: true });else
      navigate(`/authentication?next=${encodeURIComponent(redirectTo)}`);
    } catch (err) {
      const msg = err?.message || '';
      if (msg?.toLowerCase()?.includes('already registered') || msg?.toLowerCase()?.includes('already exists')) {
        setAuthError('An account with this email already exists. Please sign in instead.');
      } else {
        setAuthError(msg || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {authError &&
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{authError}</p>
        </div>
      }
      <Input label="Full Name" type="text" name="fullName" placeholder="John Smith"
      value={formData?.fullName} onChange={handleChange} error={errors?.fullName} required />
      <Input label="Email Address" type="email" name="email" placeholder="you@company.com"
      value={formData?.email} onChange={handleChange} error={errors?.email} required />
      <Input label="Password" type="password" name="password" placeholder="Create a strong password (8+ characters)"
      value={formData?.password} onChange={handleChange} error={errors?.password} required />
      <Input label="Confirm Password" type="password" name="confirmPassword" placeholder="Re-enter your password"
      value={formData?.confirmPassword} onChange={handleChange} error={errors?.confirmPassword} required />
      <div className="pt-1">
        <Checkbox
          label={
          <span className="text-sm">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Privacy Policy</a>
            </span>
          }
          name="agreeToTerms"
          checked={formData?.agreeToTerms}
          onChange={handleChange}
          error={errors?.agreeToTerms}
          size="sm" />
        
      </div>
      <Button type="submit" variant="default" size="lg" loading={isLoading} fullWidth>
        Create Free Account
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Already have an account?{' '}
        <a href="/authentication" className="text-primary hover:text-primary/80 font-medium">Sign in</a>
      </p>
    </form>);

};

// ─── Paid signup with managed Stripe Checkout ─────────────────────────────────
const PaidSignupFlow = ({
  planKey, planLabel, planPrice, billingCycle = 'monthly', redirectTo = '/dashboard',
}) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const step = 'account';
  const [checkoutStatus, setCheckoutStatus] = useState(null);
  const checkoutKeyStorage = `paid-signup-checkout-key-${planKey}-${billingCycle}`;
  const checkoutRequestKeyRef = React.useRef((() => {
    try {
      return sessionStorage.getItem(checkoutKeyStorage) || crypto.randomUUID();
    } catch {
      return crypto.randomUUID();
    }
  })());

  // Account form state
  const [formData, setFormData] = useState({
    fullName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);

  const signupDraftKey = `paid-signup-draft-${planKey}-${billingCycle}`;
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(signupDraftKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData((current) => ({ ...current, ...parsed, password: '', confirmPassword: '' }));
        setPendingUserId(parsed.pendingUserId || null);
      }
    } catch { /* storage is optional */ }
  }, [signupDraftKey]);
  useEffect(() => {
    // Keep only non-sensitive fields across a Checkout cancellation. Passwords
    // remain in memory and are handled solely by Supabase Auth.
    try {
      sessionStorage.setItem(signupDraftKey, JSON.stringify({
        fullName: formData.fullName,
        email: formData.email,
        agreeToTerms: formData.agreeToTerms,
        pendingUserId,
      }));
    } catch { /* storage is optional */ }
  }, [signupDraftKey, formData, pendingUserId]);

  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get('checkout');
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!outcome) return;
    if (outcome === 'cancel' || outcome === 'cancelled') {
      setCheckoutStatus({ state: 'canceled', message: 'Checkout was canceled. Your account details are still available.' });
      return;
    }
    if (!sessionId) return;
    let active = true;
    let attempts = 0;
    const check = async () => {
      try {
        const result = await paymentsService.getCheckoutSessionStatus(sessionId, 'plan_signup');
        if (!active) return;
        const status = String(result?.status || result?.state || '').toLowerCase();
        if (['fulfilled', 'complete', 'completed', 'succeeded', 'paid'].includes(status)) {
          try { sessionStorage.removeItem(signupDraftKey); } catch { /* ignore */ }
          try { sessionStorage.removeItem(checkoutKeyStorage); } catch { /* ignore */ }
          setCheckoutStatus({ state: 'fulfilled' });
        } else if (['failed', 'expired', 'canceled', 'cancelled'].includes(status)) {
          checkoutRequestKeyRef.current = crypto.randomUUID();
          try { sessionStorage.setItem(checkoutKeyStorage, checkoutRequestKeyRef.current); } catch { /* ignore */ }
          setCheckoutStatus({ state: status, message: result?.message || 'Checkout did not complete. Please try again.' });
        } else {
          setCheckoutStatus({ state: 'processing' });
          if (++attempts < 40) setTimeout(check, Math.min(5000, 1500 + attempts * 250));
          else setCheckoutStatus({ state: 'failed', message: 'Activation is taking longer than expected. Refresh this page to check again.' });
        }
      } catch {
        if (!active) return;
        setCheckoutStatus({ state: 'processing' });
        if (++attempts < 40) setTimeout(check, Math.min(5000, 1500 + attempts * 250));
        else setCheckoutStatus({ state: 'failed', message: 'We could not refresh activation status. Refresh this page to try again.' });
      }
    };
    check();
    return () => { active = false; };
  }, [signupDraftKey]);

  const validateAccount = () => {
    const e = {};
    if (!formData?.fullName?.trim() || formData?.fullName?.trim()?.length < 2)
    e.fullName = 'Full name must be at least 2 characters';
    if (!formData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email))
    e.email = 'Please enter a valid email address';
    if (!formData?.password || formData?.password?.length < 8)
    e.password = 'Password must be at least 8 characters';
    if (formData?.password !== formData?.confirmPassword)
    e.confirmPassword = 'Passwords do not match';
    if (!formData?.agreeToTerms)
    e.agreeToTerms = 'You must agree to the terms and conditions';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (authError) setAuthError('');
  };

  const handleCreateAccount = async (e) => {
    e?.preventDefault();
    if (!validateAccount()) return;
    setIsCreating(true);
    try {
      try { sessionStorage.setItem(checkoutKeyStorage, checkoutRequestKeyRef.current); } catch { /* ignore */ }
      const { data: currentAuth } = await supabase.auth.getUser();
      const currentUser = currentAuth?.user;
      if (currentUser && currentUser.email?.toLowerCase() !== formData.email.toLowerCase()) {
        throw new Error('This email does not match the account currently signed in.');
      }
      if (!currentUser && pendingUserId) {
        throw new Error('Confirm your email, sign in, then return here to continue to secure Checkout.');
      }
      const signup = currentUser
        ? { user: currentUser, session: true }
        : await signUp(formData.email, formData.password, {
            full_name: formData.fullName.trim(),
            plan_type: 'free',
            checkout_pending: true,
          });
      if (signup?.user?.id && signup.user.id !== pendingUserId) {
        setPendingUserId(signup.user.id);
        try {
          const saved = JSON.parse(sessionStorage.getItem(signupDraftKey) || '{}');
          sessionStorage.setItem(signupDraftKey, JSON.stringify({ ...saved, pendingUserId: signup.user.id }));
        } catch { /* storage is optional */ }
      }
      if (!currentUser && !signup?.session) {
        throw new Error('Check your inbox to confirm your email, then sign in and return here to continue to secure Checkout.');
      }
      const checkout = await paymentsService.createPlanSignupCheckout({
        plan: planKey,
        billingCycle,
        account: {
          fullName: formData.fullName.trim(),
          email: formData.email,
        },
        idempotencyKey: checkoutRequestKeyRef.current,
      });
      window.location.assign(checkout.url);
    } catch (err) {
      const msg = err?.message || '';
      if (msg?.toLowerCase()?.includes('already registered') || msg?.toLowerCase()?.includes('already exists')) {
        setAuthError('An account with this email already exists. Please sign in instead.');
      } else {
        setAuthError(msg || 'Account creation failed. Please try again.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // ── Step indicator ──
  const StepIndicator = () =>
  <div className="flex items-center gap-3 mb-6">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-primary text-primary-foreground">
          1
        </div>
        <span>Create Account</span>
      </div>
      <div className="flex-1 h-px bg-border" />
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-muted text-muted-foreground">
          2
        </div>
        <span>Secure Checkout</span>
      </div>
    </div>;


  // ── Account creation step ──
  if (checkoutStatus?.state === 'fulfilled') {
    return (
      <div className="text-center space-y-4 py-4">
        <Icon name="CheckCircle" size={42} className="mx-auto text-success" />
        <h3 className="text-xl font-semibold">Your plan is activated</h3>
        <p className="text-sm text-muted-foreground">Your payment and account have been confirmed.</p>
        <Button onClick={() => navigate(redirectTo, { replace: true })} fullWidth>Continue to dashboard</Button>
      </div>
    );
  }
  if (checkoutStatus?.state === 'processing') {
    return (
      <div className="text-center space-y-4 py-6">
        <Icon name="Loader2" size={34} className="mx-auto text-primary animate-spin" />
        <h3 className="text-lg font-semibold">Activating your plan</h3>
        <p className="text-sm text-muted-foreground">We are waiting for Stripe to confirm your payment. Please keep this page open.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <StepIndicator />
      {checkoutStatus && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{checkoutStatus.message || 'Checkout did not complete. Review your details and try again.'}</p>
        </div>
      )}
      <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
        <Icon name="Shield" size={16} className="text-primary flex-shrink-0" />
        <p className="text-sm text-primary font-medium">14-day free trial on all paid plans. No credit card required. 30-day money-back guarantee.</p>
      </div>
      <form onSubmit={handleCreateAccount} className="space-y-4">
        {authError &&
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{authError}</p>
          </div>
        }
        <Input label="Full Name" type="text" name="fullName" placeholder="John Smith"
        value={formData?.fullName} onChange={handleChange} error={errors?.fullName} required />
        <Input label="Email Address" type="email" name="email" placeholder="you@company.com"
        value={formData?.email} onChange={handleChange} error={errors?.email} required />
        <Input label="Password" type="password" name="password" placeholder="Create a strong password (8+ characters)"
        value={formData?.password} onChange={handleChange} error={errors?.password} required />
        <Input label="Confirm Password" type="password" name="confirmPassword" placeholder="Re-enter your password"
        value={formData?.confirmPassword} onChange={handleChange} error={errors?.confirmPassword} required />
        <div className="pt-1">
          <Checkbox
            label={
            <span className="text-sm">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Privacy Policy</a>
              </span>
            }
            name="agreeToTerms"
            checked={formData?.agreeToTerms}
            onChange={handleChange}
            error={errors?.agreeToTerms}
            size="sm" />
          
        </div>
        <Button type="submit" variant="default" size="lg" loading={isCreating} fullWidth>
          Continue to secure checkout
        </Button>
      </form>
    </div>);

};

// ─── Main Signup Page ─────────────────────────────────────────────────────────
const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const requestedPlanKey = searchParams?.get('plan')?.toLowerCase() || 'free';
  const planKey = PLAN_ORDER.includes(requestedPlanKey) ? requestedPlanKey : 'free';
  const billingCycle = searchParams?.get('billing')?.toLowerCase() === 'annual' ? 'annual' : 'monthly';
  const redirectTo = searchParams?.get('next') || '/dashboard';
  const [configuredPlan, setConfiguredPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadPlan = async () => {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*');
      const tier = data?.find((candidate) =>
        candidate?.is_active !== false &&
        normalizePlanKey(candidate?.plan_key || candidate?.tier_name) === planKey
      );
      if (!cancelled && !error && tier) setConfiguredPlan(tier);
    };
    loadPlan().catch(() => {});
    return () => { cancelled = true; };
  }, [planKey]);

  const fallbackMeta = PLAN_META?.[planKey] || PLAN_META?.free;
  const meta = configuredPlan ? {
    ...fallbackMeta,
    label: configuredPlan.tier_name || fallbackMeta.label,
    price: Number(configuredPlan.price),
    annualPrice: Number(configuredPlan.annual_price),
    annualMonthlyPrice: Number(configuredPlan.annual_monthly_price),
  } : fallbackMeta;
  const planLimits = configuredPlan ? {
    maxForms: Number(configuredPlan.max_forms),
    maxSubmissions: Number(configuredPlan.max_submissions),
    maxTeamMembers: Number(configuredPlan.max_team_members),
    maxStorageMb: Number(configuredPlan.max_storage_mb),
  } : getPlanLimits(planKey);
  const isPaid = meta?.price !== null && meta?.price > 0;

  const pageTitle = isPaid ?
  `Sign Up — ${meta?.label} Plan | WebForms` :
  'Create Free Account | WebForms';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Create your WebForms account and start building powerful forms today." />
      </Helmet>

      <div className="relative min-h-screen bg-white flex justify-center overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/2 bg-[#ecf5ee] lg:block" />
        <div className="relative z-10 w-full max-w-5xl flex">
          {/* Left branding panel */}
          <BrandingPanel planName={meta?.label} isPaid={isPaid} planKey={planKey} limits={planLimits} />

          {/* Right form panel */}
          <div className="flex-1 flex items-center justify-center p-4 md:p-8 overflow-y-auto">
            <div className="w-full max-w-md py-8">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 text-center">
              <div className="flex items-center justify-center mb-4">
                <Image
                  src="/assets/images/web-forms-logo-1769800970615.png"
                  alt="WebForms.biz logo"
                  className="w-48 h-auto object-contain" />
                
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg shadow-elevation-md p-6 md:p-8">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta?.badge}`}>
                    <Icon name={isPaid ? 'CreditCard' : 'Gift'} size={11} />
                    {meta?.label} Plan
                  </span>
                  {isPaid &&
                  <span className="text-xs text-muted-foreground">
                      {billingCycle === 'annual'
                        ? `$${meta?.annualMonthlyPrice}/mo billed annually · $${meta?.annualPrice}/yr`
                        : `$${meta?.price}/mo`}
                    </span>
                  }
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-1">
                  {isPaid ? 'Complete Your Signup' : 'Create Your Account'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isPaid ?
                  'Start with a 14-day free trial on any paid plan — no credit card required. You are also covered by our 30-day money-back guarantee.' : 'Get started for free. No credit card required.'}
                </p>
              </div>

              {/* Form content */}
              {isPaid ?
              <PaidSignupFlow
                planKey={planKey}
                planLabel={meta?.label}
                planPrice={billingCycle === 'annual' ? meta?.annualPrice : meta?.price}
                billingCycle={billingCycle}
                redirectTo={redirectTo} /> :


              <FreeSignupForm onSuccess={() => {}} redirectTo={redirectTo} />
              }
            </div>

            <p className="text-center text-xs text-muted-foreground mt-6">
              By continuing, you agree to WebForms's{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium">Privacy Policy</a>
            </p>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default SignupPage;